import { useCallback, useState } from 'react';
import { connect } from 'react-redux';

import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { MspCodesEnum } from 'APIPackage/KnmModule/enums/MspCodesEnum';
import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { KnmOkveds } from 'KnmPackage/MainModule/components/KnmOkveds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { KnmSubjectItInfo } from 'KnmPackage/MainModule/components/KnmSubjectItInfo';
import { IKnmSubject248ItAccreditationResponse } from 'APIPackage/KnmModule/interfaces/IKnmSubject248ItAccreditationResponse';

import styles from './ErknmResultItemSubjectItem.module.scss';
import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ITargetEntity } from '../../../APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { RafpRequestStatusTypeEnum } from '../../../APIPackage/KnmModule/enums/RafpRequestStatusTypeEnum';
import { KnmSonkoField } from '../../../KnmPackage/MainModule/components/KnmSonkoField';
import { ErknmResultItemSubjectInfo, KnmResultItemSubjectTypesEnum } from './ErknmResultItemSubjectInfo';
import { KnmResultItemNkoInfo } from './KnmResultItemNkoInfo';
import { KnmMspField } from '../../../KnmPackage/MainModule/components/KnmMspField';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

interface IErknmSubjectItemProps {
  checkSubjectResult: IKnmCheckSubjectResult | null;
  disableFieldByCompleteStatuses: boolean;
  disableFieldByInProcessStatuses: boolean;
  ervkId: string | undefined;
  firstOrganizationName: string | undefined;
  id: string;
  inn: string | undefined;
  isAutocomplete: IKnmErknmFormSubject['isAutocomplete'];
  isCollapsing: boolean;
  isFiz: boolean | undefined;
  isMultiFields: boolean;
  isNew: boolean;
  isNewOrganizationTemplate?: boolean;
  isOtherOrganization: boolean | undefined;
  isPresentInItRegistry: boolean | undefined;
  isSonko: boolean | undefined;
  isWatchOnly: boolean;
  itAccreditationResponse: IKnmSubject248ItAccreditationResponse | undefined;
  kpp: string | undefined;
  mspCode: MspCodesEnum | undefined;
  mspRegistryDate: IKnmErknmFormSubject['mspRegistryDate'] | undefined;
  name: string;
  ogrn: string | undefined;
  okveds: IKnmErknmFormSubject['okveds'] | undefined;
  rafpNza: string | undefined;
  removeOtherOrganization?: () => void;
  smevItAccreditationRequestStatus: string | undefined;
  smevRequestStatus: RafpRequestStatusTypeEnum | undefined;
  snils: string | undefined;
  subjectType: IDictionarySelectOption<ITargetEntity> | undefined;
  watchOnlyExtended: boolean;
}

interface IErknmSubjectItemStoreProps {
  formRef: IKnmFormRef | undefined;
}

const ErknmResultItemSubjectItemWithoutStore = ({
  checkSubjectResult,
  disableFieldByCompleteStatuses,
  disableFieldByInProcessStatuses,
  ervkId,
  firstOrganizationName,
  id,
  inn,
  isAutocomplete,
  isCollapsing,
  isFiz,
  isMultiFields,
  isNew,
  isNewOrganizationTemplate,
  isOtherOrganization,
  isPresentInItRegistry,
  isSonko,
  isWatchOnly,
  itAccreditationResponse,
  kpp,
  mspCode,
  mspRegistryDate,
  name,
  ogrn,
  okveds,
  rafpNza,
  removeOtherOrganization,
  smevItAccreditationRequestStatus,
  smevRequestStatus,
  snils,
  subjectType,
  watchOnlyExtended,
  formRef,
}: IErknmSubjectItemProps & IErknmSubjectItemStoreProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const [lookupData, setLookupData] = useState<ILegalEntityLookup | boolean | undefined>(undefined);
  const [checkByInnMessage, setCheckByInnMessage] = useState<string | undefined>(undefined);

  const getOkvedBlockId = useCallback((okvedId: string) => `${id}.okveds[${okvedId}]`, [id]);

  const remove = formRef?.current.mutators.remove;

  const makeRemoveErknmSubjectItemHandler = () => () => {
    if (!watchOnlyExtended && remove) {
      remove('organizations', 0);
    }
  };

  const isUR = subjectType?.code === KnmResultItemSubjectTypesEnum.UR;
  const isFIZ = subjectType?.code === KnmResultItemSubjectTypesEnum.FIZ;
  const isOFFICIAL = subjectType?.code === KnmResultItemSubjectTypesEnum.OFFICIAL;
  const isIFIZ = subjectType?.code === KnmResultItemSubjectTypesEnum.IFIZ;
  const isIUR = subjectType?.code === KnmResultItemSubjectTypesEnum.IUR;
  const isFIUR = subjectType?.code === KnmResultItemSubjectTypesEnum.FIUR;

  const renderSubjectInfo = () => {
    const subjectInfo = () => (
      <>
        {/* Блок информации о контролируемом лице */}
        <div className={styles.Wrapper} id={!isCollapsing ? id : undefined}>
          <ErknmResultItemSubjectInfo
            ervkId={ervkId}
            initialCheckSubjectResult={checkSubjectResult}
            id={id}
            name={name}
            isWatchOnly={isWatchOnly || !!ervkId}
            isAutocomplete={isAutocomplete}
            inn={inn}
            innLabelBlockId={`${id}.inn`}
            ogrn={ogrn}
            ogrnLabelBlockId={`${id}.ogrn`}
            firstOrganizationName={firstOrganizationName}
            organizationNameBlockId={`${id}.organizationName`}
            positionBlockId={`${id}.position`}
            isFiz={isFiz}
            subjectResult={checkSubjectResult}
            lookupData={lookupData}
            setLookupData={setLookupData}
            subjectType={subjectType}
            kppLabelBlockId={`${id}.kpp`}
            rafpLabelBlockId={`${id}.rafpNza`}
            kpp={kpp}
            smevRequestStatus={smevRequestStatus}
            rafpNza={rafpNza}
            snils={snils}
            isNewOrganizationTemplate={isNewOrganizationTemplate}
            isOtherOrganization={isOtherOrganization}
            removeOtherOrganization={removeOtherOrganization}
            setCheckByInnMessage={!ervkId ? setCheckByInnMessage : undefined}
          />

          {(isUR ||
            subjectType?.code === KnmResultItemSubjectTypesEnum.IP ||
            subjectType?.code === KnmResultItemSubjectTypesEnum.FIUR) && (
            <KnmOkveds
              name={`${name}.okveds`}
              subjectData={{ inn, ogrn }}
              watchOnly={
                watchOnlyExtended ||
                disableFieldByCompleteStatuses ||
                disableFieldByInProcessStatuses ||
                !isNewOrganizationTemplate
              }
              okveds={okveds}
              organizationAutocomplete={isAutocomplete}
              blockId={`${id}.okvedsBlock`}
              getOkvedBlockId={getOkvedBlockId}
              checkSubjectResult={checkSubjectResult}
            />
          )}

          {/* Блок информации о наличии контролируемого лица в реестре НКО-иноагентов */}
          {isUR && (
            <KnmResultItemNkoInfo name={name} ogrn={ogrn} checkByInnMessage={!ervkId ? checkByInnMessage : undefined} />
          )}

          {/* Блок информации о наличии контролируемого лица в реестре IT-компаний */}
          {(isUR || isFIUR) && (
            <KnmSubjectItInfo
              name={name}
              inn={inn}
              inspectionResults={undefined}
              isPresentInItRegistry={isPresentInItRegistry}
              itAccreditationResponse={itAccreditationResponse}
              smevItAccreditationRequestStatus={smevItAccreditationRequestStatus}
              checkByInnMessage={checkByInnMessage}
              isShowMessages={true}
              ervkId={ervkId}
            />
          )}
        </div>
      </>
    );

    const hasError = name ? checkHasErrorInCollapseBlock(formRef, name) : false;

    if (isMultiFields) {
      return subjectInfo();
    }

    return (
      <KnmCollapse
        id={id}
        theme="fields"
        title={'Контролируемое лицо'}
        isNew={isNew}
        opened={true}
        onClose={makeRemoveErknmSubjectItemHandler()}
        key={name}
        error={isKnmCollapseOpened && hasError}
        changeOpenedCallback={setIsKnmCollapseOpened}
      >
        {subjectInfo()}
      </KnmCollapse>
    );
  };

  /**
   * Рендер МСП и т.п.
   */
  const renderAdditionalInfo = () => {
    if (isFIZ || isOFFICIAL || isIFIZ || isIUR) {
      return null;
    }

    return (
      <>
        <KnmMspField
          id={`${id}.mspCode`}
          name={`${name}.mspCode`}
          value={mspCode}
          checkSubjectResult={checkSubjectResult}
          ervkId={ervkId}
          inn={inn}
          mspRegistryDate={mspRegistryDate}
          formRef={formRef}
          isShowMessages={!isFIZ}
        />

        <KnmSonkoField
          inn={inn}
          ogrn={ogrn}
          name={`${name}.isSonko`}
          subjectType={subjectType}
          value={isSonko}
          id={`${id}.isSonko`}
          checkByInnMessage={!ervkId ? checkByInnMessage : undefined}
          formRef={formRef}
          isShowMessages={true}
        />
      </>
    );
  };

  return (
    <>
      {formRef?.current.getFieldState('organizations')?.value && (
        <div className={styles.Wrapper} id={!isCollapsing ? id : undefined}>
          {renderSubjectInfo()}
          {renderAdditionalInfo()}
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmSubjectItemStoreProps => ({
  formRef: getKnmFormRef(state),
});

export const ErknmResultItemSubjectItem = connect(mapStateToProps)(ErknmResultItemSubjectItemWithoutStore);
